import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { getClerkErrorMessage, matchClerkStatus, recoverCompletedSession } from '../../lib/clerkFlow';
import { AuthField } from './components/AuthField';
import { AuthPasswordField } from './components/AuthPasswordField';
import { PasswordStrengthMeter } from './components/PasswordStrengthMeter';
import { AuthOtpField } from './components/AuthOtpField';
import { AuthServerError } from './components/AuthServerError';
import { AuthCaptcha } from './components/AuthCaptcha';
import { AuthSubmitButton } from './components/AuthSubmitButton';
import { Button } from '../../shared/ui';
import { usePageMeta } from '../../shared/hooks/usePageMeta';

const emailSchema = z.object({
  email: z.string().email('Enter a valid email'),
});

const resetSchema = z
  .object({
    code: z.string().length(6, 'Enter the 6-digit code'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  });

type EmailData = z.infer<typeof emailSchema>;
type ResetData = z.infer<typeof resetSchema>;

export function ForgotPasswordPage() {
  usePageMeta('Reset Password');
  const { signIn, setActive, isLoaded } = useSignIn();
  const [step, setStep] = useState<'email' | 'reset' | 'done'>('email');
  const [serverError, setServerError] = useState('');
  const navigate = useNavigate();

  const emailForm = useForm<EmailData>({ resolver: zodResolver(emailSchema) });
  const resetForm = useForm<ResetData>({ resolver: zodResolver(resetSchema) });

  const completeReset = async (sessionId: string) => {
    if (!setActive) return;
    await setActive({ session: sessionId });
    setStep('done');
    setTimeout(() => navigate('/app/requests', { replace: true }), 1500);
  };

  const onSendCode = async ({ email }: EmailData) => {
    if (!isLoaded || !signIn) return;
    setServerError('');
    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });
      setStep('reset');
    } catch (err: unknown) {
      setServerError(getClerkErrorMessage(err, 'Could not send reset code.'));
    }
  };

  const onReset = async ({ code, password }: ResetData) => {
    if (!isLoaded || !signIn) return;
    setServerError('');
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password,
      });
      await matchClerkStatus(result, {
        complete: async (r) => {
          await completeReset(r.createdSessionId!);
        },
        fallback: () => {
          setServerError("That code didn't work. Please try again.");
        },
      });
    } catch (err: unknown) {
      const sessionId = await recoverCompletedSession(signIn);
      if (sessionId) {
        await completeReset(sessionId);
        return;
      }
      setServerError(getClerkErrorMessage(err, 'Invalid code or password.'));
    }
  };

  if (step === 'done') {
    return (
      <div className="w-full flex flex-col items-start gap-4 step-enter">
        <div className="h-12 w-12 bg-accent rounded-2xl flex items-center justify-center">
          <CheckCircle size={22} className="text-black" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-white">Password reset</h1>
          <p className="text-white/35 text-sm mt-1">Signing you in...</p>
        </div>
        <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-accent rounded-full animate-reset-progress" />
        </div>
      </div>
    );
  }

  if (step === 'reset') {
    return (
      <div className="w-full step-enter">
        <h1 className="mb-1.5 text-center text-[clamp(32px,8vw,40px)] font-bold leading-[1.1] tracking-[-0.035em] text-white">Set new password.</h1>
        <p className="mb-8 text-center text-[#a1a1a6]">Enter the code from your email and choose a new password.</p>

        <form onSubmit={resetForm.handleSubmit(onReset)} className="flex flex-col gap-4">
          <Controller
            control={resetForm.control}
            name="code"
            defaultValue=""
            render={({ field }) => (
              <AuthOtpField
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={resetForm.formState.errors.code?.message}
              />
            )}
          />

          <AuthPasswordField
            label="New password"
            placeholder="Min. 8 characters"
            autoComplete="new-password"
            error={resetForm.formState.errors.password?.message}
            {...resetForm.register('password')}
          />
          <PasswordStrengthMeter password={resetForm.watch('password') ?? ''} />

          <AuthPasswordField
            label="Confirm password"
            placeholder="••••••••"
            autoComplete="new-password"
            error={resetForm.formState.errors.confirm?.message}
            {...resetForm.register('confirm')}
          />

          <AuthServerError message={serverError} />

          <AuthSubmitButton
            isLoaded={!!isLoaded}
            isSubmitting={resetForm.formState.isSubmitting}
            loadingText="Resetting..."
            className="mt-1"
          >
            Reset password <ArrowRight size={15} />
          </AuthSubmitButton>

          <Button
            type="button"
            variant="ghost"
            onClick={() => setStep('email')}
            className="!text-white/55 !text-xs normal-case !tracking-normal !px-0 hover:!text-white/80 hover:!bg-transparent"
          >
            ← Send a new code
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full step-enter">
      <h1 className="mb-1.5 text-center text-[clamp(32px,8vw,40px)] font-bold leading-[1.1] tracking-[-0.035em] text-white">Reset password.</h1>
      <p className="mb-8 text-center text-[#a1a1a6]">
        Remember it?{' '}
        <Link to="/login" className="text-accent hover:underline">
          Sign in
        </Link>
      </p>

      <form onSubmit={emailForm.handleSubmit(onSendCode)} className="flex flex-col gap-4">
        <AuthField
          label="Email"
          type="email"
          placeholder="you@company.com"
          autoComplete="email"
          error={emailForm.formState.errors.email?.message}
          {...emailForm.register('email')}
        />

        <AuthServerError message={serverError} />

        <AuthCaptcha />

        <AuthSubmitButton
          isLoaded={!!isLoaded}
          isSubmitting={emailForm.formState.isSubmitting}
          loadingText="Sending..."
          className="mt-1"
        >
          Send reset code <ArrowRight size={15} />
        </AuthSubmitButton>
      </form>
    </div>
  );
}
